import { RefObject, useCallback, useLayoutEffect, useRef, useState } from 'react';
import './useAdaptivePageSize.scss';

type UseAdaptivePageSizeOptions = {
  rowHeight?: number;
  minPageSize?: number;
  maxPageSize?: number;
  bottomMargin?: number;
};

type UseAdaptivePageSizeResult = {
  pageSize: number;
  containerRef: RefObject<HTMLDivElement>;
  containerHeight: number | undefined;
  recalculate: () => void;
};

export const ADAPTIVE_TABLE_CONTAINER_CLASS = 'AdaptiveTableContainer';

const DEFAULT_ROW_HEIGHT = 54;
const DEFAULT_MIN_PAGE_SIZE = 5;
const DEFAULT_MAX_PAGE_SIZE = 50;
const DEFAULT_BOTTOM_MARGIN = 16;
const RECALCULATE_DEBOUNCE_MS = 150;
const FALLBACK_PAGINATION_HEIGHT = 56;

export function useAdaptivePageSize({
  rowHeight = DEFAULT_ROW_HEIGHT,
  minPageSize = DEFAULT_MIN_PAGE_SIZE,
  maxPageSize = DEFAULT_MAX_PAGE_SIZE,
  bottomMargin = DEFAULT_BOTTOM_MARGIN,
}: UseAdaptivePageSizeOptions = {}): UseAdaptivePageSizeResult {
  const containerRef = useRef<HTMLDivElement>(null);
  const [pageSize, setPageSize] = useState(minPageSize);
  const [containerHeight, setContainerHeight] = useState<number | undefined>(undefined);

  const recalculate = useCallback(() => {
    const container = containerRef.current;
    if (!container) return;
    const { top } = container.getBoundingClientRect();
    const theadHeight = container.querySelector('.ant-table-thead')?.getBoundingClientRect().height ?? 0;
    const paginationHeight =
      container.querySelector('.ant-table-pagination')?.getBoundingClientRect().height || FALLBACK_PAGINATION_HEIGHT;
    const totalAvailable = window.innerHeight - top - bottomMargin;
    const rowsAvailable = totalAvailable - theadHeight - paginationHeight;
    const rows = Math.floor(rowsAvailable / rowHeight);
    const nextPageSize = Math.min(Math.max(rows, minPageSize), maxPageSize);
    const nextContainerHeight = Math.max(totalAvailable, theadHeight + paginationHeight);
    setPageSize(nextPageSize);
    setContainerHeight(nextContainerHeight);
  }, [rowHeight, minPageSize, maxPageSize, bottomMargin]);

  useLayoutEffect(() => {
    recalculate();

    let debounceTimer: ReturnType<typeof setTimeout>;
    const scheduleRecalculate = () => {
      clearTimeout(debounceTimer);
      debounceTimer = setTimeout(recalculate, RECALCULATE_DEBOUNCE_MS);
    };

    window.addEventListener('resize', scheduleRecalculate);

    const resizeObserver = new ResizeObserver(scheduleRecalculate);
    resizeObserver.observe(document.documentElement);

    return () => {
      clearTimeout(debounceTimer);
      window.removeEventListener('resize', scheduleRecalculate);
      resizeObserver.disconnect();
    };
  }, [recalculate]);

  return { pageSize, containerRef, containerHeight, recalculate };
}
